// Lightweight, privacy-friendly client tracking for BCH School.
//
// Sends anonymous event counts to our own Netlify Function (/.netlify/functions/stats).
// No cookies, no personal data — just increments counters server-side and, for
// audience metrics, records activity against a random anonymous visitor ID.
// Fails silently: if the backend isn't deployed (e.g. local dev), nothing breaks.

const ENDPOINT = "/.netlify/functions/stats?action=track";

const VISITOR_KEY = "bch-school:visitor-id";
const SESSION_DAY_KEY = "bch-school:session-day";

export type TrackEvent =
  | "visit"
  | "session"
  | "lesson_open"
  | "lesson_complete"
  | "module_complete"
  | "certificate_earned"
  | "share_click"
  | "wallet_click"
  | "donate_click"
  | "quiz_start"
  | "quiz_pass"
  | "quiz_fail"
  | "certificate_download"
  | "certificate_share";

interface TrackPayload {
  event: TrackEvent;
  lessonId?: string;
  /** Anonymous, randomly-generated per-browser id. No personal data. */
  visitorId?: string;
}

/**
 * Get (or lazily create) a stable, anonymous visitor id for this browser.
 * It's a random string — not tied to any personal information — used purely
 * to distinguish unique/returning students in aggregate stats.
 */
export function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private mode / disabled) — skip visitor id.
    return null;
  }
}

export function track(event: TrackEvent, extra?: { lessonId?: string }): void {
  // Only run in the browser.
  if (typeof window === "undefined") return;

  const payload: TrackPayload = {
    event,
    ...extra,
    visitorId: getVisitorId() ?? undefined,
  };
  const body = JSON.stringify(payload);

  try {
    // sendBeacon is ideal — non-blocking and survives page navigation.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }

  // Fallback: fire-and-forget fetch.
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    /* ignore — tracking must never break the app */
  });
}

/** Local calendar day key, e.g. "2026-09-09". */
function localDay(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Fire once per tab session: counts a raw "visit", and additionally sends a
 * "session" ping at most once per calendar day per browser. The daily session
 * ping is what powers accurate unique / returning / retention metrics.
 */
export function trackVisitOnce(): void {
  if (typeof window === "undefined") return;

  // Raw per-tab-session visit (kept for the visits-over-time chart).
  try {
    if (sessionStorage.getItem("bch-school:visited") !== "1") {
      sessionStorage.setItem("bch-school:visited", "1");
      track("visit");
    }
  } catch {
    track("visit");
  }

  // Daily session ping — once per calendar day, tied to the visitor id.
  try {
    const day = localDay();
    if (localStorage.getItem(SESSION_DAY_KEY) !== day) {
      localStorage.setItem(SESSION_DAY_KEY, day);
      track("session");
    }
  } catch {
    // If storage is unavailable we simply skip the daily session ping.
  }
}
